export type NavigationReason =
  | 'pushState'
  | 'replaceState'
  | 'back'
  | 'forward'
  | 'hash';

type NavigationCallback = (reason: NavigationReason) => void;

export class NavigationObserver {
  private readonly onNavigate: NavigationCallback;

  private started = false;

  private originalPushState: History['pushState'] | null = null;

  private originalReplaceState: History['replaceState'] | null = null;

  private readonly handlePopState = (): void => {
    this.onNavigate('back');
  };

  private readonly handleHashChange = (): void => {
    this.onNavigate('hash');
  };

  constructor(onNavigate: NavigationCallback) {
    this.onNavigate = onNavigate;
  }

  start(): void {
    if (this.started || typeof window === 'undefined') return;

    this.started = true;
    this.originalPushState = history.pushState.bind(history);
    this.originalReplaceState = history.replaceState.bind(history);

    history.pushState = (...args: Parameters<History['pushState']>) => {
      this.originalPushState?.(...args);
      this.onNavigate('pushState');
    };

    history.replaceState = (...args: Parameters<History['replaceState']>) => {
      this.originalReplaceState?.(...args);
      this.onNavigate('replaceState');
    };

    window.addEventListener('popstate', this.handlePopState);
    window.addEventListener('hashchange', this.handleHashChange);
  }

  destroy(): void {
    if (!this.started || typeof window === 'undefined') return;

    if (this.originalPushState) {
      history.pushState = this.originalPushState;
    }

    if (this.originalReplaceState) {
      history.replaceState = this.originalReplaceState;
    }

    window.removeEventListener('popstate', this.handlePopState);
    window.removeEventListener('hashchange', this.handleHashChange);

    this.originalPushState = null;
    this.originalReplaceState = null;
    this.started = false;
  }
}
